import { RootState, TrackingState } from '@/store/storeStateInterfaces';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import TrackingRepository from '@/api/repositories/TrackingRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import Tracking from '@/models/Tracking';
import moment from 'moment';
import TrackingPage from '@/interfaces/Page.interface';

const trackingRepository: TrackingRepository = RepositoryFactory.get('tracking');

const store: TrackingState = {
    currentTracking: undefined
};

const actions: ActionTree<TrackingState, RootState> = {
    sendTracking: async ({ commit, getters }, isOfflineData: boolean = false): Promise<void> => {
        if (getters.currentTracking && getters.currentTracking.pages.length > 0) {

            const trackingData: Tracking = getters.currentTracking;
            // set end date only if date is not an offline data
            // ATTENTION:
            // Be aware that sometimes this sendTracking method is called in a unload eventlistener
            // and the data is not send to the server, because the browser task is eliminated previously.
            // If in such a case the user starts the reader again, the saved tracking data is send and therefore
            // the previously end time need to be used and not a new one.
            if (!isOfflineData) {
                commit('setLastPageEnd', moment().toISOString());
            } else {
                trackingData.pages = trackingData.pages.filter((page) => !!page.readPageEnd);

                // --- Cause of tracking pulse normally this case should not occur.... ---
                // Special edge case! This occurs if a user just read the first page and the isOfflineData case happens.
                // Because we have no end time we need to remove the last page entry and because this is the only entry,
                // at the end we have no entry at all and therefore no tracking data.
                if (trackingData.pages.length <= 0) {
                    console.info('Edge Case: No tracking at all!');
                    commit('clearTracking');
                    return;
                }
            }

            trackingData.readSessionEnd = trackingData.pages[trackingData.pages.length - 1].readPageEnd!;

            /**
             * Format the time values for splitScreen-pages
             */
            const splittedPages: TrackingPage[] = [];
            trackingData.pages.forEach((pageObject: any) => {
                if (pageObject.splitMode) {
                    const duration = moment(pageObject.readPageEnd).diff(moment(pageObject.readPageStart), 'ms');
                    const newPageObject = {
                        page: (parseInt(pageObject.page) + 1).toString(),
                        readPageStart: moment(pageObject.readPageStart).add(duration / 2, 'ms').toISOString(),
                        readPageEnd: moment(pageObject.readPageStart).add(duration, 'ms').toISOString(),
                        splitMode: false
                    };
                    pageObject.readPageEnd = moment(pageObject.readPageStart).add(duration / 2, 'ms').toISOString();
                    splittedPages.push(pageObject);
                    splittedPages.push(newPageObject);
                } else {
                    splittedPages.push(pageObject);
                }
            });
            trackingData.pages = splittedPages;
            // ATTENTION! This data is send via sendBeacon (https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon)
            // That means this call is asynchronous but it is not possible to wait for it via await
            trackingRepository.sendTrackingData(trackingData);
        }
    }
};

const mutations: MutationTree<TrackingState> = {
    startTracking: (state: TrackingState, payload: {
        locationId: string,
        publicationId: string,
        magazineId: string,
        readerId: string,
        publisherId: string
    }) => {
        const tracking = new Tracking();
        Object.assign(tracking, payload);
        tracking.readSessionStart = moment().toISOString();
        state.currentTracking = tracking;
    },
    addTrackingPage: (state: TrackingState, payload: { page: string, splitMode: boolean }) => {
        if (state.currentTracking!.pages.length > 0) {
            state.currentTracking!.pages[state.currentTracking!.pages.length - 1]
                .readPageEnd = moment().toISOString();
        }

        state.currentTracking!.pages.push({
            page: payload.page,
            readPageStart: moment().toISOString(),
            readPageEnd: undefined, // Will be set on leaving the page!
            splitMode: payload.splitMode
        });
    },
    setLastPageEnd: (state: TrackingState, endTime?: string) => {
        // if no date passed, use create new
        if (state.currentTracking && state.currentTracking!.pages.length > 0) {
            endTime = endTime || moment().toISOString();
            state.currentTracking!.pages[state.currentTracking!.pages.length - 1]
                .readPageEnd = endTime;
        }
    },
    clearTracking: (state: TrackingState) => {
        state.currentTracking = undefined;
    }
};

const getters: GetterTree<TrackingState, RootState> = {
    currentTracking: (state: TrackingState): Tracking | undefined => state.currentTracking
};

const trackingStore: Module<TrackingState, RootState> = {
    state: store,
    actions,
    mutations,
    getters
};

export default trackingStore;
